import React, { useEffect, useRef, useState } from 'react'
import { Dialog, DialogContent, DialogTitle } from '@mui/material'
import { t } from 'i18next'
import { useDispatch } from 'react-redux'
import FilterSelector from 'Components/FilterSelector'
import MiniLoading from 'Components/MiniLoading'
import NoImage from 'Assets/Images/Logos/no-image.png'
import { fetchCompanies } from 'redux/actions/companyAction'
import { searchAdminProductsAction } from 'redux/actions/quickListSettingsActions'

const PAGE_SIZE = 10

const normalizeId = id => id?.toLowerCase?.() || id

function ProductPickerModal({ open, selectedProducts, onClose, onConfirm }) {
  const dispatch = useDispatch()

  const [companies, setCompanies] = useState([])
  const [selectedCompany, setSelectedCompany] = useState('')
  const [searchText, setSearchText] = useState('')
  const [products, setProducts] = useState([])
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [checkedIds, setCheckedIds] = useState([])
  const [checkedProductsById, setCheckedProductsById] = useState({})
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!open) return

    setCheckedIds(
      selectedProducts.map(product => normalizeId(product.ProductId))
    )
    setCheckedProductsById(
      selectedProducts.reduce((acc, product) => {
        acc[normalizeId(product.ProductId)] = product
        return acc
      }, {})
    )
    setSearchText('')
    setSelectedCompany('')
    setPageNumber(1)
    setProducts([])
    setHasMore(true)
  }, [open])

  useEffect(() => {
    if (!open) return

    const searchCompanies = async () => {
      try {
        const data = await dispatch(fetchCompanies())
        setCompanies(data)
      } catch (error) {
        setCompanies([])
      }
    }

    searchCompanies().then()
  }, [open])

  const searchProducts = async (page, replace) => {
    const requestId = ++requestIdRef.current
    setLoading(true)

    try {
      const payload = {
        GlobalExecution: false,
        CompanyId: selectedCompany || null,
        IsActive: true,
        PageNumber: page,
        PageSize: PAGE_SIZE,
        SubCategoryId: null,
        SearchText: searchText || null,
      }
      const data = await dispatch(searchAdminProductsAction(payload))

      if (requestId !== requestIdRef.current) return

      setProducts(prev => (replace ? data : [...prev, ...data]))
      setHasMore(data.length === PAGE_SIZE)
      setPageNumber(page)
    } catch (error) {
      if (requestId !== requestIdRef.current) return
      if (replace) setProducts([])
      setHasMore(false)
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }

  useEffect(() => {
    if (!open) return

    searchProducts(1, true).then()
  }, [open, selectedCompany])

  const handleSearchSubmit = event => {
    event.preventDefault()
    searchProducts(1, true).then()
  }

  const handleLoadMore = () => {
    searchProducts(pageNumber + 1, false).then()
  }

  const handleListScroll = event => {
    if (loading || !hasMore) return

    const { scrollTop, scrollHeight, clientHeight } = event.target
    if (scrollHeight - scrollTop - clientHeight < 100) {
      handleLoadMore()
    }
  }

  const toggleProduct = (product, checked) => {
    const productId = normalizeId(product.ProductId)

    setCheckedIds(prev =>
      checked
        ? Array.from(new Set([...prev, productId]))
        : prev.filter(id => id !== productId)
    )

    if (checked) {
      setCheckedProductsById(prev => ({
        ...prev,
        [productId]: {
          ...product,
          Quantity: prev[productId]?.Quantity ?? product.Quantity,
        },
      }))
    }
  }

  const handleConfirm = () => {
    const confirmedProducts = checkedIds.map(id => {
      const existing = checkedProductsById[id]
      return {
        ProductId: id,
        ProductName: existing?.ProductName,
        Sku: existing?.Sku,
        Images: existing?.Images || [],
        Quantity: existing?.Quantity ?? 1,
      }
    })
    onConfirm(confirmedProducts)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="modal-product-picker">
      <DialogTitle>
        {t('QUICK_LIST_SETTINGS.PICKER.PRODUCTS_TITLE')}
      </DialogTitle>
      <DialogContent>
        <div className="quick-list-picker-filters">
          <FilterSelector
            label={companies.find(c => c.CompanyId === selectedCompany)?.Name}
            options={companies}
            getOptionValue={item => item.CompanyId}
            getOptionLabel={item => item.Name}
            value={selectedCompany}
            onChange={item => setSelectedCompany(item.CompanyId)}
            placeholder={t('QUICK_LIST_SETTINGS.PICKER.COMPANY')}
          />
          <form
            onSubmit={handleSearchSubmit}
            className="quick-list-picker-search">
            <input
              type="text"
              className="form-control"
              placeholder={t('QUICK_LIST_SETTINGS.PICKER.SEARCH_PLACEHOLDER')}
              value={searchText}
              onChange={event => setSearchText(event.target.value)}
              data-testid="input-product-search"
            />
            <button type="submit" className="btn-add">
              {t('QUICK_LIST_SETTINGS.PICKER.SEARCH_BUTTON')}
            </button>
          </form>
        </div>

        <div className="quick-list-picker-list" onScroll={handleListScroll}>
          {products.length === 0 && !loading ? (
            <p>{t('QUICK_LIST_SETTINGS.PICKER.NO_RESULTS')}</p>
          ) : (
            products.map(product => {
              const productId = normalizeId(product.ProductId)
              const isChecked = checkedIds.includes(productId)
              return (
                <label
                  key={productId}
                  className={`quick-list-picker-item${
                    isChecked ? ' selected' : ''
                  }`}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={event =>
                      toggleProduct(product, event.target.checked)
                    }
                    data-testid={`checkbox-product-${productId}`}
                  />
                  <img
                    src={product.Images?.[0]?.URL || NoImage}
                    alt={product.ProductName || ''}
                    width={40}
                    height={40}
                    className="quick-list-picker-thumb"
                  />
                  <span className="quick-list-picker-item-name">
                    {product.ProductName}
                  </span>
                  <span className="quick-list-picker-item-sku">
                    {product.Sku}
                  </span>
                </label>
              )
            })
          )}
          {loading && <MiniLoading noMargin />}
        </div>

        <div className="quick-list-picker-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onClose}
            data-testid="btn-cancel-product-picker">
            {t('QUICK_LIST_SETTINGS.PICKER.CANCEL')}
          </button>
          <button
            type="button"
            className="btn-save"
            onClick={handleConfirm}
            data-testid="btn-confirm-product-picker">
            {t('QUICK_LIST_SETTINGS.PICKER.CONFIRM')} ({checkedIds.length})
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ProductPickerModal
